/* eslint-disable no-nested-ternary */
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { useDocumentTitle } from "../../../hooks/useDocumentTitle";
import { GenericTokenDto } from "../../../model/user/dto/GenericTokenDto";
import { SignupDto } from "../../../model/user/dto/SignupDto";
import { Spacing } from "../../../theme/Spacing";
import { RegisterWrapper } from "../RegisterWrapper";
import { SignupEmail } from "./SignupEmail";
import { SignupToken } from "./SignupToken";
import { clearPendingSignup, readPendingSignup } from "./signupProgress";

const TogglePageButton = styled(Button)`
  background-color: ${(props) => props.theme.transparent};
  margin: 0;
  color: ${(props) => props.theme.main};
  padding: 0;
`;

const ResponsiveFlex = styled(Flex)`
  @media (min-width: 360px) and (max-width: 990px) {
    padding: ${Spacing.m};
  }
`;

enum SIGNUP_STEPS {
  EMAIL = "EMAIL",
  TOKEN = "TOKEN",
}

export const Signup: React.FC = () => {
  // Restored from sessionStorage so a refresh mid-flow resumes on the code step
  // instead of dropping the user back on the first screen.
  const [pendingSignup] = useState(readPendingSignup);

  const [step, setStep] = useState<SIGNUP_STEPS>(
    pendingSignup ? SIGNUP_STEPS.TOKEN : SIGNUP_STEPS.EMAIL,
  );
  const formMethods = useForm<SignupDto & GenericTokenDto>({
    defaultValues: {
      name: pendingSignup?.name ?? "",
      email: pendingSignup?.email ?? "",
      password: "",
      token: "",
    },
  });

  const backToEmailStep = ({ keepIdentity }: { keepIdentity: boolean }) => {
    clearPendingSignup();
    formMethods.reset(
      keepIdentity
        ? { ...formMethods.getValues(), password: "", token: "" }
        : { name: "", email: "", password: "", token: "" },
    );
    setStep(SIGNUP_STEPS.EMAIL);
  };

  // "Wrong email? Start over" - the user wants a clean slate.
  const onRestart = () => backToEmailStep({ keepIdentity: false });

  // The pending signup lapsed (backend can't recover it). Keep name + email so
  // the user only has to re-enter their password to get a fresh code.
  const onSessionExpired = () => backToEmailStep({ keepIdentity: true });

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { search } = useLocation();
  useDocumentTitle(`${t("app.name").toUpperCase()} - ${t("common.signup")}`);

  return (
    <RegisterWrapper>
      <ResponsiveFlex
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        <FormProvider {...formMethods}>
          {step === SIGNUP_STEPS.EMAIL ? (
            <SignupEmail updateStep={() => setStep(SIGNUP_STEPS.TOKEN)} />
          ) : (
            <SignupToken
              onRestart={onRestart}
              onSessionExpired={onSessionExpired}
            />
          )}
        </FormProvider>
      </ResponsiveFlex>

      <ResponsiveFlex gap="s" alignItems="center">
        <Text fontSize="s" lineHeight={"normal"}>
          {t("sign_in.has_account")}
        </Text>
        <TogglePageButton
          onClick={() => navigate(`/login${search}`)}
          title={t("common.login")}
        />
      </ResponsiveFlex>
    </RegisterWrapper>
  );
};
